// src/pages/FinancePage.js
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// Import UI Components
import UniversalSearch from '../components/UniversalSearch';
import ParticipantCard from '../components/ParticipantCard';
import ParticipantDetailModal from '../components/ParticipantDetailModal';
import ParticipantRegistrationModal from '../components/ParticipantRegistrationModal';
import PipelineStats from '../components/PipelineStats';

// Import Material-UI Components
import {
  Divider,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Box,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const FinancePage = () => {
  const { user } = useAuth(); // Get the current user to check their role
  const [searchResults, setSearchResults] = useState([]);
  const [clearedList, setClearedList] = useState([]);
  const [selectedParticipantId, setSelectedParticipantId] = useState(null);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);

  // Function to fetch the list of participants already cleared
  const fetchClearedList = async () => {
    try {
      // We use the staff-accessible endpoint to get recently cleared participants
      const response = await api.get('/api/workflow/cleared-list');
      // The backend already sorts by time, but we can re-sort just in case
      const cleared = response.data.sort((a, b) => new Date(b.FinanceClearanceTime) - new Date(a.FinanceClearanceTime));
      setClearedList(cleared);
    } catch (err) {
      console.error("Could not fetch the list of cleared participants.", err);
    }
  };

  // Fetch the initial list when the component first loads
  useEffect(() => {
    fetchClearedList();
  }, []);

  // Handler for the search component
  const handleSearch = async (query) => {
    try {
        const response = await api.get(`/api/participants/search?query=${query}&status=all`);
        setSearchResults(response.data);
    } catch (err) {
        console.error("Search failed:", err);
        alert("Failed to perform search. Please try again.");
    }
  };
  
  // Handlers for opening and closing the participant detail modal
  const openDetailModal = (participantId) => setSelectedParticipantId(participantId);
  const closeDetailModal = (updated) => {
    setSelectedParticipantId(null);
    if (updated) {
      setSearchResults([]); // Clear search results to force a fresh search
      fetchClearedList(); // Refresh the "Recently Cleared" list
    }
  };

  // Handler for when the on-site registration is successful
  const handleRegistrationSuccess = (newParticipantId) => {
    setIsRegisterModalOpen(false); // Close the registration form
    setSelectedParticipantId(newParticipantId); // Immediately open the detail modal for the new participant
  };

  return (
    <>
      <PipelineStats />
      
      <UniversalSearch
        onSearch={handleSearch}
        title="Finance Clearance"
        placeholder="Search for any participant to clear..."
      />
      
      {/* The "Register New Participant" button is only visible to Admin and Finance roles */}
      {(user?.role === 'Admin' || user?.role === 'Finance') && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setIsRegisterModalOpen(true)}>
            Register New Participant
          </Button>
        </Box>
      )}

      {/* Display search results as participant cards */}
      {searchResults.map(p => (
        <ParticipantCard
          key={p.ParticipantID}
          participant={p}
          onAction={openDetailModal}
          pipeline="finance" // Pass the current pipeline context to the card
        />
      ))}

      <Divider sx={{ my: 4 }}>
        <Typography>Recently Cleared</Typography>
      </Divider>
      
      <Paper>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Clearance Time</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clearedList.map((p) => (
                <TableRow hover key={p.ParticipantID}>
                  <TableCell>{p.FullName}</TableCell>
                  <TableCell>{p.EmailAddress}</TableCell>
                  <TableCell>{p.CountryName}</TableCell>
                  <TableCell>{p.FinanceClearanceTime ? new Date(p.FinanceClearanceTime).toLocaleString() : 'N/A'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Render modals conditionally to ensure they are not always in the DOM */}
      {selectedParticipantId && (
        <ParticipantDetailModal 
          participantId={selectedParticipantId}
          open={!!selectedParticipantId}
          onClose={closeDetailModal}
        />
      )}

      <ParticipantRegistrationModal
        open={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onRegistrationSuccess={handleRegistrationSuccess}
      />
    </>
  );
};

export default FinancePage;